import { createAsyncThunk } from '@reduxjs/toolkit';
import { signOut } from 'aws-amplify/auth';
import axios, { type AxiosProgressEvent, type Method } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { clearAllChatDrafts } from 'src/helper/chatDraftStore';
import { getCorrelationId } from 'src/helper/correlation-id';
import { logger, normalizeError } from 'src/helper/logger';
import { isMaintenanceResponse } from 'src/helper/maintenanceMode';
import { clearStoredViewConfigs } from 'src/helper/viewConfigStore';
import { resetUserProfileBootGuard } from 'src/hooks/userProfileBootGuard';
import { maintenanceModeDetected } from 'src/redux/maintenance/slice';
import { sessionRedirect } from 'src/shared/utils/sessionRedirect';
import { clearAllVendorBannerSessionLatches } from 'src/shared/utils/vendorWelcomeBanner';
import { resetWorkTypeDiagnosticsThrottle } from 'src/shared/utils/workTypeDiagnostics';

// Backend calls that exceed this bound are treated as stalled so the UI can
// surface an error state instead of an eternal skeleton rather than hanging on
// a dead connection forever. It sits above the gateway timeout (so the backend
// stays the authority on slow-but-valid requests and the client only trips as a
// dead-connection backstop) and is generous enough to cover large media uploads.
const DEFAULT_REQUEST_TIMEOUT_MS = 5 * 60_000;

type OptionsType = {
  method: Method;
  url: string;
  contentType?: 'json' | 'form-data';
  params?: any;
  data?: any;
  token?: string;
  userPoolDetails?: any;
  signal?: AbortSignal;
  shouldAddSocketId?: boolean;
  extraHeaders?: Record<string, string>;
  onUploadProgress?: (e: AxiosProgressEvent) => void;
};

// Format Request Options
export function requestOptions(
  method: OptionsType['method'],
  url: OptionsType['url'],
  params: OptionsType['params'],
  contentType: OptionsType['contentType'],
  data: OptionsType['data'],
  token: OptionsType['token'],
  userPoolDetails?: any,
  signal?: any,
  shouldAddSocketId?: boolean,
  extraHeaders?: Record<string, string>,
) {
  const socketId =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('socket_id') : null;
  const options = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Correlation-ID': getCorrelationId(),
      ...(socketId && shouldAddSocketId ? { 'X-Socket-ID': socketId } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!isEmpty(userPoolDetails)
        ? {
            'User-Pool-Id': userPoolDetails?.user_pool_id,
          }
        : {}),
      ...(extraHeaders ?? {}),
    },
    method,
    url,
    params,
    data,
    signal,
    timeout: DEFAULT_REQUEST_TIMEOUT_MS,
  };
  if (method !== 'POST' && method !== 'PUT' && method !== 'PATCH' && params) {
    options.params = params;
  } else if (contentType === 'json') {
    options.headers['Content-Type'] = 'application/json';
    options.params = '';
    options.data = params;
  } else if (contentType === 'form-data') {
    options.headers['Content-Type'] = 'multipart/form-data';
    options.data = params;
    options.params = '';
  } else {
    options.data = params;
  }
  return options;
}

// Common Request

export const request = (
  options: OptionsType,
  name: string,
  passToPayload?: { [key: string]: any },
) =>
  createAsyncThunk(name, async (payload: any, thunkAPI) => {
    try {
      const res = await axios({
        ...requestOptions(
          options.method,
          options.url,
          payload,
          options.contentType,
          options.data,
          options.token,
          options.userPoolDetails,
          options.signal,
          options.shouldAddSocketId,
          options.extraHeaders,
        ),
        ...(options.onUploadProgress ? { onUploadProgress: options.onUploadProgress } : {}),
      });

      if (passToPayload) {
        return { ...res.data, ...passToPayload };
      }

      return res.data;
    } catch (error: unknown) {
      const errorDetails = error as {
        response?: { status?: number; data?: Record<string, unknown> };
        code?: string;
      };
      if (errorDetails?.response?.status === 401) {
        try {
          // A 401 right after login means the account authenticated but the
          // backend denied it (e.g. no access to this portal). Flag it as an
          // unauthorized account so the login page can explain why, instead of
          // redirecting silently. Any other 401 is treated as a session expiry.
          /* istanbul ignore else -- SSR/Node has no sessionStorage; this catch only runs for client-dispatched requests */
          if (typeof window !== 'undefined') {
            // Its OWN try/catch, deliberately: sessionStorage throws in Safari with
            // "block all cookies", Safari private mode (QuotaExceededError), sandboxed
            // iframes and when storage is full. Sharing a try with `await signOut()`
            // below would let a storage failure skip the sign-out entirely — the user
            // would be redirected while the remote session stayed live, and the catch
            // would log "signOut() failed" for a call that never ran. The reason flag is
            // cosmetic; terminating the session is not.
            try {
              const isPostLogin = sessionStorage.getItem('postLoginVerification') === 'true';
              sessionStorage.removeItem('postLoginVerification');
              sessionStorage.setItem(isPostLogin ? 'unauthorizedAccess' : 'sessionExpired', 'true');
              if (isPostLogin) {
                logger.warn('[Auth] Authenticated account denied access after login', {
                  url: options.url,
                });
              }
            } catch (storageError) {
              logger.warn('[Auth] Could not record the 401 reason flag; continuing with sign-out', {
                error: normalizeError(storageError),
                url: options.url,
              });
            }
          }
          await signOut();
        } catch (signOutError) {
          // APX-556: a 401 often means degraded connectivity, so signOut() itself
          // can reject. Swallowing the error here (instead of rethrowing) is
          // deliberate — the session must ALWAYS terminate locally and land the
          // user on /login. Rethrowing would skip the redirect below and leave the
          // user authenticated on the current page in a retry loop.
          //
          // Amplify rejects BEFORE it clears tokens (assertTokenProviderConfig throws
          // first; clientSignOut/globalSignOut swallow their own network errors), so
          // reaching here means the Cognito cookies are very likely still valid. Wipe
          // them ourselves — otherwise "terminated locally" is false and the user lands
          // on /login still holding a token the server verifies.
          sessionRedirect.clearCognitoCookies();
          logger.warn(
            '[Auth] signOut() failed during forced 401 sign-out; wiping credentials locally',
            {
              error: normalizeError(signOutError),
              url: options.url,
            },
          );
        } finally {
          // Runs whether or not signOut() succeeded, so a failed signOut() can
          // never skip the local session teardown or the /login redirect (APX-556).
          //
          // The redirect goes FIRST on purpose. A `location.pathname` assignment does
          // not halt synchronous execution, so everything below still runs — but this
          // ordering means no teardown call added to this block later can preempt the
          // redirect, which is APX-556's core guarantee. (Four have already accreted
          // here; a throw from any of them escapes this `finally`, bypassing the
          // rejectWithValue below and the `unauthorized` shape isCanceledError() needs.)
          sessionRedirect.toLogin();
          // Clear the profile boot fan-out guard so a new same-tab session after
          // this forced sign-out (session expiry / unauthorized) re-runs the
          // profile → lookup → views → counter boot instead of staying latched.
          resetUserProfileBootGuard();
          // ORI-677: this forced sign-out hard-navigates in the SAME tab, so sessionStorage
          // (and the welcome-banner session latch) survives. Clear it here too — a routine
          // token expiry ejects the SAME vendor back into the SAME tab with the SAME
          // onboarding_id, so a surviving latch would re-show a banner that should be
          // suppressed on the next login (AC-1). This is the most common real logout, and
          // must stay in sync with AuthContext.signOut / signOutAndClearOnly.
          clearAllVendorBannerSessionLatches();
          // ORI-654: the unresolved-work-type diagnostic keeps a module-level throttle Map;
          // clear it on teardown (matching the boot guard / banner latches) so one vendor's
          // throttle history never suppresses the next same-tab session's diagnostics.
          // Merged into the `finally` (it arrived on main inside the old `try`), so like the
          // rest of the teardown it now also runs when signOut() rejects — a widening of
          // ORI-654's contract that follows from APX-556, not a change of its intent.
          resetWorkTypeDiagnosticsThrottle();
          // ORI-734: a 401 forced sign-out is a same-tab teardown too — clear the per-conversation
          // chat drafts so the expiring session's unsent staged files never restore for the next
          // account, keeping this in sync with AuthContext.signOut / signOutAndClearOnly.
          // Merged into the `finally` (it arrived on main inside the old `try`) for the same
          // reason as ORI-654 above: the teardown must also run when signOut() rejects.
          clearAllChatDrafts();
          clearStoredViewConfigs();
        }
        // Reject so any in-flight .unwrap().then(...) success handlers and
        // fulfilled reducers don't run against `null` during the logout
        // redirect. isCanceledError() treats this shape as suppressible.
        return thunkAPI.rejectWithValue({
          actionType: 'unauthorized',
          status: 401,
          ...(passToPayload || {}),
        });
      } else if (
        isMaintenanceResponse(errorDetails?.response?.status, errorDetails?.response?.data)
      ) {
        // The backend is in maintenance mode (`php artisan down`), so EVERY
        // in-flight and subsequent request is failing for the same reason.
        // Raise the app-wide flag once — the reducer is idempotent, so the
        // fan-out of a whole page's worth of failing requests still results in
        // a single window — and let MaintenanceGate take over the screen.
        thunkAPI.dispatch(maintenanceModeDetected());
        logger.warn('[API] Backend is in maintenance mode', {
          url: options.url,
          method: options.method,
        });
        // Rejected with the same shape as the 401 branch so isCanceledError()
        // suppresses the error toasts and rejection handlers every call site
        // already gates on — otherwise the maintenance window would be buried
        // under one "something went wrong" toast per failed request.
        return thunkAPI.rejectWithValue({
          actionType: 'maintenance',
          status: 503,
          ...(passToPayload || {}),
        });
      } else if (axios.isCancel(error)) {
        // Request was canceled, no need to handle this error
        return thunkAPI.rejectWithValue({ actionType: 'canceled', ...(passToPayload || {}) });
      } else {
        if (!errorDetails?.response) {
          logger.warn('[API] Network failure — no server response', {
            error: normalizeError(error),
            url: options.url,
            method: options.method,
            code: errorDetails?.code,
          });
        }
        return thunkAPI.rejectWithValue({
          ...(errorDetails?.response?.data || {}),
          status: errorDetails?.response?.status,
          ...(passToPayload || {}),
        });
      }
    }
  });

export function isCanceledError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as Record<string, unknown>;
  if (err.actionType === 'canceled') return true;
  // A mid-session 401 triggers a logout redirect; existing call sites already
  // skip toasts/handlers on canceled requests, so we opt into the same seam
  // instead of asking every caller to add a second check.
  if (err.actionType === 'unauthorized') return true;
  // Maintenance mode blacks out the whole app behind MaintenanceGate, so every
  // request rejected by it is expected noise — same seam as the 401 above.
  if (err.actionType === 'maintenance') return true;
  if (axios.isCancel(error)) return true;
  if (err.code === 'ERR_CANCELED') return true;
  if (err.name === 'AbortError') return true;
  return false;
}

// A rejection shaped like the backend definitively saying the entity is gone —
// the same GET will never succeed on retry, so callers that close a detail
// panel and drop its URL param on rejection should do so ONLY for these
// statuses. Everything else (429, 5xx, network/no-status, ...) is a plausible
// transient: the entity may still be valid and a later refresh can succeed.
export function isEntityGoneRejection(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status = (error as Record<string, unknown>).status;
  return status === 404 || status === 403 || status === 410;
}

export function getBaseUrl(): string {
  return process.env.NEXT_PUBLIC_API_BASE_URL || '';
}

export function getPusherKey() {
  return process.env.NEXT_PUBLIC_PUSHER_APP_KEY || '';
}
